import type {
  FieldValues,
  UseFormReturn,
  UseFormProps,
  SubmitHandler,
} from 'react-hook-form';

import { useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import useStateWithLocalStorage from './useStateWithLocalStorage';

interface UseFormWithState<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
> extends UseFormProps<TFieldValues, TContext> {
  formName: string;
}

export const useFormWithState = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues extends FieldValues | undefined = undefined,
>({
  formName,
  ...args
}: UseFormWithState<TFieldValues, TContext>): UseFormReturn<
  TFieldValues,
  TContext,
  TTransformedValues
> => {
  const [formState, setState] = useStateWithLocalStorage<TFieldValues>(
    `FormState_${formName}`,
    args.defaultValues
  );
  const form = useForm<TFieldValues, TContext, TTransformedValues>({
    ...args,
    defaultValues: formState,
  });

  useEffect(() => {
    const subscription = form.watch((values) => setState(values));
    return () => subscription.unsubscribe();
  }, [form.watch]);

  const handleSubmit = (fn: SubmitHandler<FieldValues>) => {
    return form.handleSubmit((data) => {
      setState(null);
      return fn(data);
    });
  };

  return { ...form, handleSubmit };
};
